import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LanguageCode } from '../helper-types/general/consts/language-code';

i18n.use(initReactI18next).init({
    resources: {
        en: {
            translation: {
                generic: {
                    confirmationDialog: {
                        title: 'Confirm Deletion',
                        description: 'Are you sure you want to delete this item? This action cannot be undone.',
                        confirmButton: 'Delete',
                        cancelButton: 'Cancel'
                    }
                },
                nav: {
                    login: 'Login',
                    mainMenu: 'Main Menu',
                    home: 'Home',
                    patients: 'Patients',
                    appointments: 'Waiting Queue',
                    prescriptions: 'Prescriptions',
                    security: 'Security',
                    profile: 'Profile',
                    users: 'Users (Employees)',
                    roles: 'Jobs',
                    physicianSubscriptions: 'Physician Subscriptions'
                },
                auth: {
                    login: {
                        welcome: 'Welcome to Eyadtek',
                        loginButton: 'Log In',
                        title: 'Log In',
                        loginError: 'An error occurred during login. Please try again.',
                        forgotPassword: 'Forgot your password?',
                        rememberMe: 'Remember me',
                        noAccount: 'Don\'t have an account?',
                        contactAdmin: 'Contact Sales Engineer',
                        resetPassword: 'Reset Password',
                        resetPasswordInstructions: 'Please contact the sales engineer to reset your password:',
                        features: {
                            patientRecordsTitle: 'Patient Records Management',
                            patientRecordsDescription: 'Easily manage patient information, medical history, and treatment plans in a secure, centralized system.',
                            appointmentTitle: 'Appointment Scheduling',
                            appointmentDescription: 'Streamline appointment booking and management with an intuitive interface for staff and patients.',
                            securityTitle: 'Secure Data Handling',
                            securityDescription: 'Ensure patient data privacy with robust security measures and compliance with healthcare regulations.',
                            analyticsTitle: 'Reporting and Analytics',
                            analyticsDescription: 'Generate detailed reports and insights to improve patient care and operational efficiency.'
                        }
                    },
                    signOut: 'Sign Out',
                    signIn: 'Sign In',
                },
                home: {
                    welcome: 'Welcome to {{clinicName}}',
                    EyadtiSystem: 'Eyadti System',
                    userInfoCard: {
                        systemDashboard: "Eyadti System Dashboard",
                        tipsHeader: "💡 System Tips & Best Practices",
                        tipCounter: "Tip {{current}} of {{total}}",
                        tips: {
                            addingPatients: {
                                title: "Adding Patients",
                                description: "Always verify patient information before adding them to the system. Review the name spelling, phone number, and WhatsApp number if available."
                            },
                            queueManagement: {
                                title: "Queue Management",
                                description: "Prioritize patients based on the severity of their condition. Track the waiting queue carefully."
                            },
                            patientPrivacy: {
                                title: "Patient Privacy",
                                description: "Always log out when leaving the clinic. Patient information is confidential and protected by privacy laws."
                            },
                            efficientWorkflow: {
                                title: "Efficient Workflow",
                                description: "Update patient waiting status in real-time. Change the waiting status to 'Completed' or 'Cancelled' to keep the queue moving smoothly."
                            },
                            communication: {
                                title: "Communication",
                                description: "When adding a patient to the waiting queue, write your notes if any, to communicate important information to the doctor."
                            }
                        }
                    },
                    statistics: {
                        // Existing
                        patients: 'Patients',
                        appointments: 'Appointments',
                        completed: 'Completed',
                        cancelled: 'Cancelled',
                        prescriptions: 'Prescriptions',
                        totalRevenue: 'Total Revenue',
                        // Section headers
                        todayOverview: "Today's Overview",
                        thisMonthPerformance: "This Month's Performance",
                        allTimeStatistics: 'All-Time Statistics',
                        analytics: 'Analytics',
                        // Today section
                        appointmentsToday: 'Appointments Today',
                        pendingAppointments: 'Pending Appointments',
                        completionRate: 'Completion Rate',
                        // This month section
                        newPatients: 'New Patients',
                        revenue: 'Revenue',
                        // All-time section
                        totalPatients: 'Total Patients',
                        totalAppointments: 'Total Appointments',
                        totalPrescriptions: 'Total Prescriptions',
                        firstTime: 'First-Time',
                        followUp: 'Follow-Up',
                        // Analytics section
                        averagePatientAge: 'Average Patient Age',
                        years: 'years',
                        noData: 'No statistics available',
                        errors: {
                            fetchError: 'Failed to fetch statistics.'
                        }
                    },
                    badges: {
                        title: 'Achievements',
                        patients: '{{num}} Patients',
                        patientsDesc: 'Treated {{num}} patients',
                        appointments: '{{num}} Appointments',
                        appointmentsDesc: 'Completed {{num}} appointments',
                        prescriptions: '{{num}} Prescriptions',
                        prescriptionsDesc: 'Issued {{num}} prescriptions',
                        perfectionist: 'Exceptional',
                        perfectionistDesc: 'Achieved 99%+ completion rate',
                        excellence: 'Excellence',
                        excellenceDesc: 'Achieved 95%+ completion rate',
                        reliable: 'Reliable',
                        reliableDesc: 'Achieved 90%+ completion rate',
                        firstPatient: 'First Steps',
                        firstPatientDesc: 'Treated your first patient',
                        busyDay: 'Busy Day',
                        busyDayDesc: '10+ appointments in a single day',
                        progress: 'complete'
                    }
                },
                pwa: {
                    updateAvailable: 'Update available',
                    updateMessage: 'A new version of the app is available. Would you like to update now?',
                    appInstalled: 'App installed successfully!',
                    appInstalledMessage: 'You can now use this app offline.',
                    installPrompt: 'Install the app',
                    installPromptMessage: 'Install this app on your device for a better experience.',
                    installButton: 'Install',
                    cancelButton: 'Not now',
                },
                patients: {
                    title: 'Patients',
                    addPatient: 'Add Patient',
                    addPatientSubtitle: 'Enter patient information to create a new record',
                    editPatient: 'Edit Patient',
                    deletePatient: 'Delete Patient',
                    enqueueAppointment: 'Enqueue Appointment',
                    patientDetails: 'Patient Details',
                    age: 'Age',
                    gender: 'Gender',
                    male: 'Male',
                    female: 'Female',
                    unknown: 'Unknown',
                    tableTitle: 'Patients Table',
                    tableSubtitle: 'View, search, and manage all patient records',
                    whatsAppNumber: 'WhatsApp Number',
                    noPatientsFound: 'No patients found',
                    personalInfo: 'Personal Information',
                    contactInfo: 'Contact Information',
                    deleteConfirmation: 'Are you sure you want to delete this patient? This action cannot be undone.',
                    deleteConfirmationTitle: 'Confirm Patient Deletion',
                    unknownPatient: 'Unknown Patient',
                    errors: {
                        fetchError: 'Failed to fetch patients. Please try again.',
                        updateError: 'Failed to {{action}} patient. Something went wrong.',
                        deleteError: 'An error occurred while deleting the patient.',
                        firstNameRequired: 'First name is required',
                        firstNameMaxLength: 'First name must not exceed {{max}} characters',
                        lastNameRequired: 'Last name is required',
                        lastNameMaxLength: 'Last name must not exceed {{max}} characters',
                        ageRequired: 'Age is required',
                        ageMax: 'Age must not exceed {{max}} years',
                        genderRequired: 'Gender is required',
                        phoneNumberRequired: 'Phone number is required',
                        phoneNumberInvalid: 'Try entering a valid Egyptian phone number',
                        whatsAppNumberRequired: 'WhatsApp number is required',
                        whatsAppNumberInvalid: 'Try entering a valid Egyptian WhatsApp number',
                        pageNumberMin: 'Page number must be greater than or equal to 1',
                        pageSizeMin: 'Page size must be greater than or equal to 1'
                    }
                },
                appointments: {
                    title: 'Appointments',
                    addAppointment: 'Add Appointment',
                    editAppointment: 'Edit Appointment',
                    deleteAppointment: 'Delete Appointment',
                    completeAppointment: 'Complete Appointment',
                    cancelAppointment: 'Cancel Appointment',
                    appointmentDetails: 'Appointment Details',
                    examinationType: 'Examination Type',
                    firstTime: 'First Time',
                    followUp: 'Follow Up',
                    notes: 'Notes',
                    waitingTime: 'Waiting Time',
                    searchPlaceholder: 'Search appointments ...',
                    complete: 'Complete Appointment',
                    completeConfirmation: 'Are you sure you want to mark this appointment as completed?',
                    cancel: 'Cancel Appointment',
                    cancelConfirmation: 'Are you sure you want to cancel this appointment?',
                    delete: 'Delete Appointment',
                    deleteConfirmation: 'Are you sure you want to delete this appointment? This action cannot be undone.',
                    noAppointments: 'No records found',
                    unknownPatient: 'Unknown Patient',
                    enqueueAppointment: 'Enqueue Appointment',
                    patientAppointmentQueue: 'Patients Waiting Queue',
                    queueSubtitle: 'Monitor and manage today\'s patient waiting queue',
                    m: 'm',
                    h: 'h',
                    errors: {
                        enqueueError: 'Failed to enqueue appointment',
                        fetchError: 'Failed to fetch appointments. Please try again.',
                        updateError: 'Failed to {{action}} appointment. Something went wrong.',
                        patientIdPositive: 'Patient ID must be a positive number',
                        invalidExaminationType: 'Invalid examination type',
                        notesMax: 'Notes must be at most {{max}} characters',
                        idPositive: 'ID must be a positive number',
                        invalidStatus: 'Invalid appointment status'
                    }
                },
                users: {
                    title: "Users",
                    addUser: "Add User",
                    addUserSubtitle: "Create a new user account with jobs and permissions",
                    editUser: "Edit User",
                    deleteUser: "Delete User",
                    noUsersFound: "No users found",
                    accountInfo: "Account Information",
                    rolesAndStatus: "Jobs & Status",
                    physicianDetails: "Physician Details",
                    tableSubtitle: "Manage user accounts, jobs, and access permissions",
                    updateProfileSubtitle: "Update your personal and professional information",
                    deleteConfirmation: "Are you sure you want to delete this user? This action cannot be undone.",
                    deleteConfirmationTitle: "Confirm User Deletion",
                    physicianSpecialization: "Physician Specialization",
                    prescriptionColorSchemeName: "Prescriptions Color Scheme",
                    averageConsultationTimeInMinutes: "Average Consultation Time (In Minutes)",
                    clinicName: "Clinic Name",
                    clinicAddress: "Clinic Address",
                    clinicEmail: "Clinic Email",
                    clinicPhoneNumber: "Clinic Phone Number",
                    subscribedInSmsService: "Subscribed In SMS Service",
                    subscribedInAiService: "Subscribed In AI Service",
                    subscribedInWhatsAppService: "Subscribed In WhatsApp Service",
                    subscribedInPrioritySupportService: "Subscribed In Priority Support Service",
                    tableTitle: "Users Management",
                    updateProfile: "Update Profile",
                    username: "Username",
                    email: "Email",
                    totalSubscriptionFee: "Total Subscription Fee",
                    phoneNumber: "Phone Number",
                    password: "Password",
                    firstName: "First Name",
                    lastName: "Last Name",
                    fullName: "Full Name",
                    firstTimeExaminationFee: "First Time Examination Fee",
                    followUpExaminationFee: "Follow Up Examination Fee",
                    feeCurrency: "Fee Currency",
                    currencyName: {
                        sar: "SAR",
                        egp: "EGP"
                    },
                    gender: "Gender",
                    address: "Address",
                    roles: "Jobs",
                    isActive: "Is Active",
                    specialization: "Specialization",
                    clinicInfo: "Clinic Info",
                    changePassword: "Change Password",
                    changePasswordTitle: "Change Password",
                    newPassword: "New Password",
                    confirmingPassword: "Confirming Password",
                    errors: {
                        addFailed: "Failed to add user. Please try again.",
                        updateFailed: "Failed to update user. Please try again.",
                        deleteFailed: "Failed to delete user. Please try again.",
                        fetchFailed: "Failed to fetch users",
                        submitFailed: "Failed to submit form. Please try again.",
                        fetchError: "Error fetching user data:",
                        updateError: "Error updating user:",
                        deleteError: "Error deleting user:",
                        userNotFound: "User not found",
                        invalidRole: "Invalid job",
                        firstNameRequired: "First name is required",
                        firstNameMaxLength: "First name must not exceed {{max}} characters",
                        lastNameRequired: "Last name is required",
                        lastNameMaxLength: "Last name must not exceed {{max}} characters",
                        phoneNumberRequired: "Phone number is required",
                        phoneNumberInvalid: "Phone number must be a valid Egyptian one",
                        emailRequired: "Email is required",
                        emailInvalid: "Email must be in a valid format",
                        passwordRequired: "Password is required",
                        passwordInvalid: "Password must be in a valid format",
                        rolesRequired: "Jobs ids cannot be empty",
                        confirmingPasswordRequired: "Confirming password is required",
                        newPasswordRequired: "New password is required",
                        newPasswordMatch: "Confirming password must match the new password",
                        physicianSpecializationInvalid: "Invalid physician specialization",
                        prescriptionColorSchemeNameInvalid: "You should select a color scheme for the prescriptions papers",
                        averageConsultationTimeMustBeGreaterThanZero: "Average consultation time must be greater than zero",
                        firstTimeExaminationFeeCannotBeNegative: "First time examination fee cannot be negative",
                        followUpExaminationFeeCannotBeNegative: "Follow up examination fee cannot be negative",
                        currencyInvalid: "Invalid currency value",
                        clinicNameRequired: "Clinic name is required",
                        clinicNameMaxLength: "Clinic name must not exceed {{max}} characters",
                        clinicAddressRequired: "Clinic address is required",
                        clinicAddressMaxLength: "Clinic address must not exceed {{max}} characters",
                        clinicEmailInvalid: "Clinic email must be in a valid format",
                        clinicEmailMaxLength: "Clinic email must not exceed {{max}} characters",
                        clinicPhoneNumberRequired: "Clinic phone number is required",
                        clinicPhoneNumberInvalid: "Clinic phone number must be a valid Egyptian one",
                        pageNumberMin: "Page number must be greater than or equal to 1",
                        pageSizeMin: "Page size must be greater than or equal to 1"
                    }
                },
                roles: {
                    title: 'Jobs',
                    tableTitle: 'Jobs',
                    tableSubtitle: 'Define jobs and assign permissions to control access',
                    addRole: 'Add Job',
                    addRoleSubtitle: 'Create a new job with specific permissions',
                    editRole: 'Edit Job',
                    deleteRole: 'Delete Job',
                    noRolesFound: 'No jobs found',
                    name: 'Name',
                    claims: 'Claims',
                    createdAt: 'Created At',
                    deleteConfirmation: 'Are you sure you want to delete this job? This action cannot be undone.',
                    deleteConfirmationTitle: 'Confirm Job Deletion',
                    systemClaims: {
                        accessPatientsGrid: 'Access Patients Page',
                        accessAppointmentsGrid: 'Access Waiting Queue Page',
                        accessPrescriptionsGrid: 'Access Prescriptions Page',
                        accessUserManagementMenu: 'Access User Management Page',
                    },
                    systemRoles: {
                        administrator: 'Administrator',
                        physician: 'Physician',
                    },
                    errors: {
                        deleteError: 'An error occurred while deleting the job.',
                        addFailed: 'Failed to add job. Please try again.',
                        updateFailed: 'Failed to update job. Please try again.',
                        deleteFailed: 'Failed to delete job. Please try again.',
                        fetchFailed: 'Failed to fetch job data:',
                        fetchError: 'Error fetching job data:',
                        updateError: 'Error updating job:',
                        roleNameMaxLength: 'Job name must not exceed {{max}} characters',
                        claimsNotEmpty: 'Claims must not be empty',
                        pageNumberMin: 'Page number must be greater than or equal to 1',
                        pageSizeMin: 'Page size must be greater than or equal to 1'
                    }
                },
                physicians: {
                    subscriptionsTitle: 'Physician Subscriptions',
                    subscriptionsSubtitle: 'Manage physician accounts and service subscriptions',
                    fullName: 'Full Name',
                    email: 'Email',
                    phoneNumber: 'Phone Number',
                    clinicName: 'Clinic Name',
                    smsService: 'SMS Service',
                    aiService: 'AI Service',
                    whatsAppService: 'WhatsApp Service',
                    prioritySupportService: 'Priority Support Service',
                    totalFee: 'Total Fee',
                    activate: 'Activate',
                    deactivate: 'Deactivate',
                    activateConfirmationTitle: 'Confirm Activation',
                    activateConfirmation: 'Are you sure you want to activate this physician account? This will also activate all associated tenant accounts.',
                    deactivateConfirmationTitle: 'Confirm Deactivation',
                    deactivateConfirmation: 'Are you sure you want to deactivate this physician account? This will also deactivate all associated tenant accounts.',
                    noPhysiciansFound: 'No physicians found',
                    errors: {
                        statusChangeError: 'An error occurred while changing the account status.'
                    }
                },
                drugs: {
                    title: 'Drug',
                    addDrug: 'Add Drug',
                    brandName: 'Brand Name',
                    genericName: 'Generic Name',
                    commonName: 'Common Name',
                    dosageForms: {
                        tablet: 'Tablet',
                        capsule: 'Capsule',
                        suspension: 'Suspension',
                        syrup: 'Syrup',
                        injection: 'Injection',
                        inhaler: 'Inhaler',
                        nebulizerSolution: 'Nebulizer Solution',
                        cream: 'Cream',
                        gel: 'Gel',
                        eyeDrop: 'Eye Drop',
                        suppository: 'Suppository',
                        oralSolution: 'Oral Solution',
                        powder: 'Powder'
                    },
                    errors: {
                        brandNameRequired: 'Brand name is required',
                        brandNameMaxLength: 'Brand name must not exceed {{max}} characters',
                        genericNameMaxLength: 'Generic name must not exceed {{max}} characters',
                        commonNameMaxLength: 'Common name must not exceed {{max}} characters'
                    }
                },
                medicalAnalysis: {
                    title: 'Medical Analysis',
                    add: 'Add Medical Analysis',
                    name: 'Name',
                    description: 'Description',
                    result: 'Result',
                    errors: {
                        nameRequired: 'Analysis name is required',
                        nameMaxLength: 'Analysis name must not exceed {{max}} characters',
                        descriptionMaxLength: 'Description must not exceed {{max}} characters'
                    }
                },
                prescriptions: {
                    title: 'Prescriptions',
                    tableTitle: 'Prescriptions Table',
                    tableSubtitle: 'View and manage all patient prescriptions',
                    addPrescription: 'Add Prescription',
                    addPrescriptionSubtitle: 'Create a new prescription for a patient',
                    editPrescription: 'Edit Prescription',
                    deletePrescription: 'Delete Prescription',
                    sendWhatsApp: 'Send via WhatsApp',
                    printPrescription: 'Print Prescription',
                    prescriptionDetails: 'Prescription Details',
                    dateIssued: 'Date Issued',
                    diagnosis: 'Diagnosis',
                    patientName: 'Patient Name',
                    patientData: 'Patient Data',
                    noPrescriptionsFound: 'No prescriptions found',
                    deleteConfirmation: 'Are you sure you want to delete this prescription? This action cannot be undone.',
                    pdfOpenedForPrinting: 'Prescription PDF opened for printing',
                    prescriptionSentViaWhatsApp: 'Prescription sent via WhatsApp successfully',
                    selectPatient: 'Select Patient',
                    currentPatient: 'Current Patient',
                    appointmentAndDiagnosis: 'Appointment and Diagnosis',
                    medicalHistory: 'Medical History',
                    drugsAndAnalyses: 'Drugs and Analyses',
                    availableDrugs: 'Available Drugs',
                    selectedDrugs: 'Selected Drugs',
                    availableAnalyses: 'Available Analyses',
                    selectedAnalyses: 'Selected Analyses',
                    addDrug: 'Add New Drug',
                    deleteDrug: 'Delete Drug',
                    deleteItemConfirmation: 'Are you sure you want to delete this {{item}}? This action cannot be undone.',
                    examineWithAi: 'Examine with AI',
                    askAi: 'Ask AI',
                    examinationWithAi: 'Examination with AI',
                    symptomsDescription: 'Patient symptoms description',
                    examine: 'Examine',
                    examinationTypes: {
                        firstTime: 'First Time',
                        followUp: 'Follow Up'
                    },
                    examinationRecommendation: 'Examination Recommendation (Using AI)',
                    defaultDoseAndUnit: '500 mg',
                    defaultFrequencyAndRoute: 'Take 2 times a day',
                    defaultInstructions: 'Take with food',
                    searchDrugs: 'Search Drugs',
                    noDrugsSelected: 'No drugs selected',
                    dosageForm: 'Dosage Form',
                    doseAndUnit: 'Dose & Unit',
                    frequencyAndRoute: 'Frequency & Route',
                    instructions: 'Instructions',
                    drugs: 'Drugs',
                    medicalAnalyses: 'Medical Analyses',
                    searchAnalyses: 'Search Analyses',
                    noAnalysesSelected: 'No analyses selected',
                    result: 'Result',
                    addMedicalAnalysis: 'Add Medical Analysis',
                    deleteMedicalAnalysis: 'Delete Medical Analysis',
                    medicalHistoryForPatient: 'Medical History for Patient',
                    noDiagnosisProvided: 'No diagnosis provided',
                    noMedicalHistoryAvailable: 'No medical history available',
                    loadingMedicalHistory: 'Loading medical history...',
                    errors: {
                        patientRequired: 'You must select a patient appointment',
                        symptomsRequired: 'Symptoms are required',
                        diagnosisMaxLength: 'Diagnosis must not exceed {{max}} characters',
                        promptRequired: 'Prompt is required',
                        invalidDosageForm: 'Invalid dosage form',
                        pageNumberMin: 'Page number must be greater than or equal to 1',
                        pageSizeMin: 'Page size must be greater than or equal to 1',
                        deletePrescriptionError: 'An error occurred while deleting the prescription',
                        sendWhatsAppError: 'An error occurred while sending the prescription via WhatsApp',
                        retrievePdfError: 'Failed to retrieve prescription PDF',
                        failedToGetResponse: 'Failed to get response, try again',
                        fetchPdfError: 'An error occurred while fetching the prescription PDF',
                        failedToAddDrug: 'Failed to add drug',
                        failedToAddDrugTryAgain: 'Failed to add drug. Please try again.',
                        failedToAddMedicalAnalysis: 'Failed to add medical analysis',
                        failedToAddMedicalAnalysisTryAgain: 'Failed to add medical analysis. Please try again.'
                    }
                },
                common: {
                    switchLanguage: 'Switch Language',
                    switchToRTL: 'Switch to RTL',
                    switchToLTR: 'Switch to LTR',
                    lightMode: 'Light Mode',
                    darkMode: 'Dark Mode',
                    actions: 'Actions',
                    processing: 'Processing...',
                    ok: 'OK',
                    save: 'Save',
                    complete: 'Complete',
                    cancel: 'Cancel',
                    loading: 'Loading...',
                    loadingData: 'Loading data...',
                    notAvailable: 'N/A',
                    add: 'Add',
                    edit: 'Edit',
                    delete: 'Delete',
                    close: 'Close',
                    submit: 'Submit',
                    filterPanel: 'Search Panel',
                    applyFilters: 'Apply Filters',
                    clearFilters: 'Clear Filters',
                    clear: 'Clear',
                    search: 'Search',
                    active: 'Active',
                    inactive: 'Inactive',
                    all: 'All',
                    yes: 'Yes',
                    no: 'No',
                    notFound: {
                        icon: '404 icon',
                        title: 'Page Not Found',
                        description: 'The page you\'re looking for doesn\'t exist or has been moved. Try returning to the home page.'
                    },
                    unauthorized: {
                        icon: 'Lock icon',
                        title: 'Unauthorized Access',
                        description: 'You do not have permission to access this page. Please contact the administrator if you believe this is an error.'
                    },
                    backToHome: 'Back to Home',
                    firstName: 'First Name',
                    lastName: 'Last Name',
                    email: 'Email',
                    phoneNumber: 'Phone Number',
                    password: 'Password',
                    role: 'Job',
                    roles: 'Jobs',
                    isActive: 'Is Active',
                    submitting: 'Processing...',
                    pagination: {
                        rowsPerPage: 'Rows per page',
                        displayedRows: 'Rows {{from}}-{{to}} of {{count}}',
                    },
                    errors: {
                        required: 'This field is required'
                    }
                }
            }
        },
        ar: {
            translation: {
                generic: {
                    confirmationDialog: {
                        title: 'تأكيد الحذف',
                        description: 'هل أنت متأكد من رغبتك في حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء.',
                        confirmButton: 'حذف',
                        cancelButton: 'إلغاء'
                    }
                },
                nav: {
                    login: 'تسجيل الدخول',
                    mainMenu: 'القائمة الرئيسية',
                    home: 'الرئيسية',
                    patients: 'المرضى',
                    appointments: 'قائمة الانتظار',
                    prescriptions: 'الوصفات الطبية',
                    security: 'الأمان',
                    profile: 'الملف الشخصي',
                    users: 'المستخدمون (الموظفون)',
                    roles: 'الوظائف',
                    physicianSubscriptions: 'اشتراكات الأطباء'
                },
                auth: {
                    login: {
                        welcome: 'مرحبًا بك في عيادتك',
                        loginButton: 'تسجيل الدخول',
                        title: 'تسجيل الدخول',
                        loginError: 'حدث خطأ أثناء تسجيل الدخول. الرجاء المحاولة مرة أخرى.',
                        forgotPassword: 'هل نسيت كلمة المرور؟',
                        rememberMe: 'تذكرني',
                        noAccount: 'ليس لديك حساب؟',
                        contactAdmin: 'تواصل مع مهندس المبيعات',
                        resetPassword: 'إعادة تعيين كلمة المرور',
                        resetPasswordInstructions: 'الرجاء التواصل مع مهندس المبيعات لإعادة تعيين كلمة المرور:',
                        features: {
                            patientRecordsTitle: 'إدارة سجلات المرضى',
                            patientRecordsDescription: 'إدارة معلومات المرضى، التاريخ الطبي، وخطط العلاج بسهولة في نظام آمن ومركزي.',
                            appointmentTitle: 'جدولة المواعيد',
                            appointmentDescription: 'تبسيط حجز وإدارة المواعيد بواجهة سهلة الاستخدام للطاقم والمرضى.',
                            securityTitle: 'التعامل الآمن مع البيانات',
                            securityDescription: 'ضمان خصوصية بيانات المرضى مع تدابير أمنية قوية والامتثال للوائح الرعاية الصحية.',
                            analyticsTitle: 'التقارير والتحليلات',
                            analyticsDescription: 'إنشاء تقارير وتحليلات تفصيلية لتحسين رعاية المرضى والكفاءة التشغيلية.'
                        }
                    },
                    signOut: 'تسجيل الخروج',
                    signIn: 'تسجيل الدخول',
                },
                home: {
                    welcome: 'مرحبًا بك في {{clinicName}}',
                    EyadtiSystem: 'نظام عيادتي',
                    userInfoCard: {
                        systemDashboard: "لوحة تحكم نظام عيادتي",
                        tipsHeader: "💡 نصائح النظام وأفضل الممارسات",
                        tipCounter: "نصيحة {{current}} من {{total}}",
                        tips: {
                            addingPatients: {
                                title: "إضافة المرضى",
                                description: "تحقق دائمًا من معلومات المريض قبل إضافتها إلى النظام. راجع تهجئة الاسم، وأيضًا رقم الهاتف ورقم الواتس آب في حالة توفرهما."
                            },
                            queueManagement: {
                                title: "إدارة قائمة الانتظار",
                                description: "حدد أولوية المرضى بناءً على مدى خطورة الحالة. تتبع قائمة الانتظار بشكل جيد."
                            },
                            patientPrivacy: {
                                title: "خصوصية المريض",
                                description: "قم دائمًا بتسجيل الخروج عند مغادرة باب العيادة. معلومات المريض سرية ومحمية بموجب قوانين الخصوصية."
                            },
                            efficientWorkflow: {
                                title: "سير العمل الفعال",
                                description: "قم بتحديث حالة انتظار المريض في الوقت الفعلي. قم بتغيير حالة الانتظار إلى \"مكتمل\" أو \"ملغى\" للحفاظ على تحرك قائمة الانتظار بسلاسة."
                            },
                            communication: {
                                title: "التواصل",
                                description: "عند إدراج مريض في قائمة الانتظار قم بكتابة ملاحظاتك إن وجدت، لإيصال المعلومات المهمة إلى الطبيب."
                            }
                        }
                    },
                    statistics: {
                        // Existing
                        patients: 'المرضى',
                        appointments: 'المواعيد',
                        completed: 'مكتمل',
                        cancelled: 'ملغى',
                        prescriptions: 'الوصفات الطبية',
                        totalRevenue: 'إجمالي الإيرادات',
                        // Section headers
                        todayOverview: 'نظرة عامة على اليوم',
                        thisMonthPerformance: 'أداء هذا الشهر',
                        allTimeStatistics: 'الإحصائيات الكلية',
                        analytics: 'التحليلات',
                        // Today section
                        appointmentsToday: 'مواعيد اليوم',
                        pendingAppointments: 'المواعيد المعلقة',
                        completionRate: 'معدل الإنجاز',
                        // This month section
                        newPatients: 'مرضى جدد',
                        revenue: 'الإيرادات',
                        // All-time section
                        totalPatients: 'إجمالي المرضى',
                        totalAppointments: 'إجمالي المواعيد',
                        totalPrescriptions: 'إجمالي الوصفات',
                        firstTime: 'كشف أول',
                        followUp: 'متابعة',
                        // Analytics section
                        averagePatientAge: 'متوسط عمر المرضى',
                        years: 'سنة',
                        noData: 'لا توجد إحصائيات متاحة',
                        errors: {
                            fetchError: 'فشل جلب الإحصائيات.'
                        }
                    },
                    badges: {
                        title: 'الإنجازات',
                        patients: '{{num}} مريض',
                        patientsDesc: 'عالجت {{num}} مريض',
                        appointments: '{{num}} موعد',
                        appointmentsDesc: 'أكملت {{num}} موعد',
                        prescriptions: '{{num}} وصفة',
                        prescriptionsDesc: 'أصدرت {{num}} وصفة طبية',
                        perfectionist: 'استثنائي',
                        perfectionistDesc: 'حققت معدل إنجاز +٩٩%',
                        excellence: 'التميز',
                        excellenceDesc: 'حققت معدل إنجاز +٩٥%',
                        reliable: 'الموثوقية',
                        reliableDesc: 'حققت معدل إنجاز +٩٠%',
                        firstPatient: 'الخطوة الأولى',
                        firstPatientDesc: 'عالجت أول مريض لك',
                        busyDay: 'يوم مزدحم',
                        busyDayDesc: '+١٠ مواعيد في يوم واحد',
                        progress: 'مكتمل'
                    }
                },
                pwa: {
                    updateAvailable: 'تحديث متاح',
                    updateMessage: 'نسخة جديدة من التطبيق متاحة. هل ترغب في التحديث الآن؟',
                    appInstalled: 'تم تثبيت التطبيق بنجاح!',
                    appInstalledMessage: 'يمكنك الآن استخدام التطبيق دون اتصال.',
                    installPrompt: 'تثبيت التطبيق',
                    installPromptMessage: 'قم بتثبيت التطبيق على جهازك لتجربة أفضل.',
                    installButton: 'تثبيت',
                    cancelButton: 'ليس الآن'
                },
                patients: {
                    title: 'المرضى',
                    addPatient: 'إضافة مريض',
                    addPatientSubtitle: 'أدخل معلومات المريض لإنشاء سجل جديد',
                    editPatient: 'تعديل مريض',
                    deletePatient: 'حذف مريض',
                    enqueueAppointment: 'إدراج موعد في قائمة الانتظار',
                    patientDetails: 'تفاصيل المريض',
                    age: 'العمر',
                    gender: 'النوع',
                    male: 'ذكر',
                    female: 'أنثى',
                    unknown: 'غير معروف',
                    tableTitle: 'جدول المرضى',
                    tableSubtitle: 'عرض وبحث وإدارة جميع سجلات المرضى',
                    whatsAppNumber: 'رقم واتساب',
                    noPatientsFound: 'لم يتم العثور على مرضى',
                    personalInfo: 'المعلومات الشخصية',
                    contactInfo: 'معلومات الاتصال',
                    deleteConfirmation: 'هل أنت متأكد من رغبتك في حذف هذا المريض؟ لا يمكن التراجع عن هذا الإجراء.',
                    deleteConfirmationTitle: 'تأكيد حذف المريض',
                    unknownPatient: 'مريض غير معروف',
                    errors: {
                        fetchError: 'فشل جلب بيانات المرضى. الرجاء المحاولة مرة أخرى.',
                        updateError: 'فشل {{action}} المريض. حدث خطأ ما.',
                        deleteError: 'حدث خطأ أثناء حذف المريض.',
                        firstNameRequired: 'الاسم الأول مطلوب',
                        firstNameMaxLength: 'الاسم الأول يجب ألا يتجاوز {{max}} حرفًا',
                        lastNameRequired: 'اسم العائلة مطلوب',
                        lastNameMaxLength: 'اسم العائلة يجب ألا يتجاوز {{max}} حرفًا',
                        ageRequired: 'العمر مطلوب',
                        ageMax: 'العمر يجب ألا يتجاوز {{max}} سنة',
                        genderRequired: 'النوع مطلوب',
                        phoneNumberRequired: 'رقم الهاتف مطلوب',
                        phoneNumberInvalid: 'حاول إدخال رقم هاتف مصري صالح',
                        whatsAppNumberRequired: 'رقم واتساب مطلوب',
                        whatsAppNumberInvalid: 'حاول إدخال رقم واتساب مصري صالح',
                        pageNumberMin: 'رقم الصفحة يجب أن يكون أكبر من أو يساوي 1',
                        pageSizeMin: 'حجم الصفحة يجب أن يكون أكبر من أو يساوي 1'
                    }
                },
                appointments: {
                    title: 'المواعيد',
                    addAppointment: 'إضافة موعد',
                    editAppointment: 'تعديل موعد',
                    deleteAppointment: 'حذف موعد',
                    completeAppointment: 'إكمال الموعد',
                    cancelAppointment: 'إلغاء الموعد',
                    appointmentDetails: 'تفاصيل الموعد',
                    examinationType: 'نوع الفحص',
                    firstTime: 'المرة الأولى',
                    followUp: 'متابعة',
                    notes: 'ملاحظات',
                    waitingTime: 'وقت الانتظار',
                    searchPlaceholder: 'البحث عن مواعيد ...',
                    complete: 'إكمال الموعد',
                    completeConfirmation: 'هل أنت متأكد من رغبتك في تعليم هذا الموعد كمكتمل؟',
                    cancel: 'إلغاء الموعد',
                    cancelConfirmation: 'هل أنت متأكد من رغبتك في إلغاء هذا الموعد؟',
                    delete: 'حذف الموعد',
                    deleteConfirmation: 'هل أنت متأكد من رغبتك في حذف هذا الموعد؟ لا يمكن التراجع عن هذا الإجراء.',
                    noAppointments: 'لم يتم العثور على سجلات',
                    unknownPatient: 'مريض غير معروف',
                    enqueueAppointment: 'إدراج موعد في قائمة الانتظار',
                    patientAppointmentQueue: 'قائمة انتظار المرضى',
                    queueSubtitle: 'مراقبة وإدارة قائمة انتظار المرضى اليوم',
                    m: 'د',
                    h: 'س',
                    errors: {
                        enqueueError: 'فشل إدراج الموعد في قائمة الانتظار',
                        fetchError: 'فشل جلب المواعيد. الرجاء المحاولة مرة أخرى.',
                        updateError: 'فشل {{action}} الموعد. حدث خطأ ما.',
                        patientIdPositive: 'معرف المريض يجب أن يكون رقمًا موجبًا',
                        invalidExaminationType: 'نوع فحص غير صالح',
                        notesMax: 'الملاحظات يجب ألا تتجاوز {{max}} حرفًا',
                        idPositive: 'المعرف يجب أن يكون رقمًا موجبًا',
                        invalidStatus: 'حالة الموعد غير صالحة'
                    }
                },
                users: {
                    title: 'المستخدمون',
                    addUser: 'إضافة مستخدم',
                    addUserSubtitle: 'إنشاء حساب مستخدم جديد مع الوظائف والصلاحيات',
                    editUser: 'تعديل مستخدم',
                    deleteUser: 'حذف مستخدم',
                    noUsersFound: 'لم يتم العثور على مستخدمين',
                    accountInfo: 'معلومات الحساب',
                    rolesAndStatus: 'الوظائف والحالة',
                    physicianDetails: 'تفاصيل الطبيب',
                    tableSubtitle: 'إدارة حسابات المستخدمين والوظائف وصلاحيات الوصول',
                    updateProfileSubtitle: 'تحديث معلوماتك الشخصية والمهنية',
                    deleteConfirmation: 'هل أنت متأكد من رغبتك في حذف هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء.',
                    deleteConfirmationTitle: 'تأكيد حذف المستخدم',
                    physicianSpecialization: 'تخصص الطبيب',
                    prescriptionColorSchemeName: 'مخطط ألوان الوصفات',
                    averageConsultationTimeInMinutes: 'متوسط مدة الاستشارة (بالدقيقة)',
                    clinicName: 'اسم العيادة',
                    clinicAddress: 'عنوان العيادة',
                    clinicEmail: 'بريد العيادة الإلكتروني',
                    clinicPhoneNumber: 'رقم هاتف العيادة',
                    subscribedInSmsService: 'الاشتراك في خدمة الرسائل القصيرة',
                    subscribedInAiService: 'الاشتراك في خدمة الذكاء الاصطناعي',
                    subscribedInWhatsAppService: 'الاشتراك في خدمة الواتساب',
                    subscribedInPrioritySupportService: 'الاشتراك في خدمة الدعم ذو الأولوية',
                    tableTitle: 'إدارة المستخدمين',
                    updateProfile: 'تحديث الملف الشخصي',
                    username: 'اسم المستخدم',
                    email: 'البريد الإلكتروني',
                    totalSubscriptionFee: 'رسوم الاشتراك الكلية',
                    phoneNumber: 'رقم الهاتف',
                    password: 'كلمة المرور',
                    firstName: 'الاسم الأول',
                    lastName: 'اسم العائلة',
                    fullName: 'الاسم الكامل',
                    firstTimeExaminationFee: 'رسوم الفحص للمرة الأولى',
                    followUpExaminationFee: 'رسوم فحص المتابعة',
                    feeCurrency: 'نوع عملة الرسوم',
                    currencyName: {
                        sar: 'الريال السعودي',
                        egp: 'الجنيه المصري'
                    },
                    gender: 'النوع',
                    address: 'العنوان',
                    roles: 'الوظائف',
                    isActive: 'نشط',
                    specialization: 'التخصص',
                    clinicInfo: 'معلومات العيادة',
                    changePassword: 'تغيير كلمة المرور',
                    changePasswordTitle: 'تغيير كلمة المرور',
                    newPassword: 'كلمة المرور الجديدة',
                    confirmingPassword: 'كلمة المرور التأكيدية',
                    errors: {
                        addFailed: 'فشل إضافة المستخدم. الرجاء المحاولة مرة أخرى.',
                        updateFailed: 'فشل تحديث المستخدم. الرجاء المحاولة مرة أخرى.',
                        deleteFailed: 'فشل حذف المستخدم. الرجاء المحاولة مرة أخرى.',
                        fetchFailed: 'فشل جلب بيانات المستخدمين',
                        fetchError: 'خطأ في جلب بيانات المستخدم:',
                        submitFailed: 'فشل إرسال البيانات. الرجاء المحاولة مرة أخرى.',
                        updateError: 'خطأ في تحديث المستخدم:',
                        deleteError: 'خطأ في حذف المستخدم:',
                        userNotFound: 'المستخدم غير موجود',
                        invalidRole: 'وظيفة غير صالحة',
                        firstNameRequired: 'الاسم الأول مطلوب',
                        firstNameMaxLength: 'الاسم الأول يجب ألا يتجاوز {{max}} حرفًا',
                        lastNameRequired: 'اسم العائلة مطلوب',
                        lastNameMaxLength: 'اسم العائلة يجب ألا يتجاوز {{max}} حرفًا',
                        phoneNumberRequired: 'رقم الهاتف مطلوب',
                        phoneNumberInvalid: 'رقم الهاتف يجب أن يكون مصريًا صالحًا',
                        emailRequired: 'البريد الإلكتروني مطلوب',
                        emailInvalid: 'البريد الإلكتروني يجب أن يكون بصيغة صالحة',
                        passwordRequired: 'كلمة المرور مطلوبة',
                        passwordInvalid: 'كلمة المرور يجب أن تكون بصيغة صالحة',
                        rolesRequired: 'معرفات الوظائف لا يمكن أن تكون فارغة',
                        confirmingPasswordRequired: 'كلمة المرور التأكيدية مطلوبة',
                        newPasswordRequired: 'كلمة المرور الجديدة مطلوبة',
                        newPasswordMatch: 'كلمة المرور التأكيدية يجب أن تطابق كلمة المرور الجديدة',
                        physicianSpecializationInvalid: 'تخصص الطبيب غير صالح',
                        prescriptionColorSchemeNameInvalid: 'يجب اختيار مخطط ألوان لأوراق الوصفات الطبية',
                        averageConsultationTimeMustBeGreaterThanZero: 'متوسط مدة الاستشارة يجب أن يكون أكبر من الصفر',
                        firstTimeExaminationFeeCannotBeNegative: 'رسوم الفحص للمرة الأولى لا يمكن أن تكون سالبة',
                        followUpExaminationFeeCannotBeNegative: 'رسوم فحص المتابعة لا يمكن أن تكون سالبة',
                        currencyInvalid: 'قيمة العملة غير صالحة',
                        clinicNameRequired: 'اسم العيادة مطلوب',
                        clinicNameMaxLength: 'اسم العيادة يجب ألا يتجاوز {{max}} حرفًا',
                        clinicAddressRequired: 'عنوان العيادة مطلوب',
                        clinicAddressMaxLength: 'عنوان العيادة يجب ألا يتجاوز {{max}} حرفًا',
                        clinicEmailInvalid: 'بريد العيادة الإلكتروني يجب أن يكون بصيغة صالحة',
                        clinicEmailMaxLength: 'بريد العيادة الإلكتروني يجب ألا يتجاوز {{max}} حرفًا',
                        clinicPhoneNumberRequired: 'رقم هاتف العيادة مطلوب',
                        clinicPhoneNumberInvalid: 'رقم هاتف العيادة يجب أن يكون مصريًا صالحًا',
                        pageNumberMin: 'رقم الصفحة يجب أن يكون أكبر من أو يساوي 1',
                        pageSizeMin: 'حجم الصفحة يجب أن يكون أكبر من أو يساوي 1'
                    }
                },
                roles: {
                    title: 'الوظائف',
                    tableTitle: 'الوظائف',
                    tableSubtitle: 'تحديد الوظائف وتعيين الصلاحيات للتحكم في الوصول',
                    addRole: 'إضافة وظيفة',
                    addRoleSubtitle: 'إنشاء وظيفة جديدة بصلاحيات محددة',
                    editRole: 'تعديل وظيفة',
                    deleteRole: 'حذف وظيفة',
                    noRolesFound: 'لم يتم العثور على وظائف',
                    name: 'الاسم',
                    claims: 'الصلاحيات',
                    createdAt: 'تاريخ الإنشاء',
                    deleteConfirmation: 'هل أنت متأكد من رغبتك في حذف هذه الوظيفة؟ لا يمكن التراجع عن هذا الإجراء.',
                    deleteConfirmationTitle: 'تأكيد حذف الوظيفة',
                    systemClaims: {
                        accessPatientsGrid: 'الوصول إلى صفحة المرضى',
                        accessAppointmentsGrid: 'الوصول إلى صفحة قائمة الانتظار',
                        accessPrescriptionsGrid: 'الوصول إلى صفحة الوصفات',
                        accessUserManagementMenu: 'الوصول إلى صفحات إدارة المستخدمين',
                    },
                    systemRoles: {
                        administrator: 'مدير',
                        physician: 'طبيب',
                    },
                    errors: {
                        deleteError: 'حدث خطأ أثناء حذف الوظيفة.',
                        addFailed: 'فشل إضافة الوظيفة. الرجاء المحاولة مرة أخرى.',
                        updateFailed: 'فشل تحديث الوظيفة. الرجاء المحاولة مرة أخرى.',
                        deleteFailed: 'فشل حذف الوظيفة. الرجاء المحاولة مرة أخرى.',
                        fetchFailed: 'فشل جلب بيانات الوظيفة:',
                        fetchError: 'خطأ في جلب بيانات الوظيفة:',
                        updateError: 'خطأ في تحديث الوظيفة:',
                        roleNameMaxLength: 'اسم الوظيفة يجب ألا يتجاوز {{max}} حرفًا',
                        claimsNotEmpty: 'الصلاحيات لا يمكن أن تكون فارغة',
                        pageNumberMin: 'رقم الصفحة يجب أن يكون أكبر من أو يساوي 1',
                        pageSizeMin: 'حجم الصفحة يجب أن يكون أكبر من أو يساوي 1'
                    }
                },
                physicians: {
                    subscriptionsTitle: 'اشتراكات الأطباء',
                    subscriptionsSubtitle: 'إدارة حسابات الأطباء واشتراكات الخدمات',
                    fullName: 'الاسم الكامل',
                    email: 'البريد الإلكتروني',
                    phoneNumber: 'رقم الهاتف',
                    clinicName: 'اسم العيادة',
                    smsService: 'خدمة الرسائل',
                    aiService: 'خدمة الذكاء الاصطناعي',
                    whatsAppService: 'خدمة الواتساب',
                    prioritySupportService: 'خدمة الدعم ذو الأولوية',
                    totalFee: 'الرسوم الكلية',
                    activate: 'تفعيل',
                    deactivate: 'إلغاء التفعيل',
                    activateConfirmationTitle: 'تأكيد التفعيل',
                    activateConfirmation: 'هل أنت متأكد من رغبتك في تفعيل حساب هذا الطبيب؟ سيؤدي هذا أيضًا إلى تفعيل جميع الحسابات المرتبطة.',
                    deactivateConfirmationTitle: 'تأكيد إلغاء التفعيل',
                    deactivateConfirmation: 'هل أنت متأكد من رغبتك في إلغاء تفعيل حساب هذا الطبيب؟ سيؤدي هذا أيضًا إلى إلغاء تفعيل جميع الحسابات المرتبطة.',
                    noPhysiciansFound: 'لم يتم العثور على أطباء',
                    errors: {
                        statusChangeError: 'حدث خطأ أثناء تغيير حالة الحساب.'
                    }
                },
                drugs: {
                    title: 'الدواء',
                    addDrug: 'إضافة دواء',
                    brandName: 'اسم العلامة التجارية',
                    genericName: 'الاسم العام',
                    commonName: 'الاسم الشائع',
                    dosageForms: {
                        tablet: 'قرص',
                        capsule: 'كبسولة',
                        suspension: 'معلق',
                        syrup: 'شراب',
                        injection: 'حقنة',
                        inhaler: 'جهاز استنشاق',
                        nebulizerSolution: 'محلول النيبولايزر',
                        cream: 'كريم',
                        gel: 'جل',
                        eyeDrop: 'قطرة عين',
                        suppository: 'لبوس',
                        oralSolution: 'محلول فموي',
                        powder: 'مسحوق'
                    },
                    errors: {
                        brandNameRequired: 'اسم العلامة التجارية مطلوب',
                        brandNameMaxLength: 'اسم العلامة التجارية يجب ألا يتجاوز {{max}} حرفًا',
                        genericNameMaxLength: 'الاسم العام يجب ألا يتجاوز {{max}} حرفًا',
                        commonNameMaxLength: 'الاسم الشائع يجب ألا يتجاوز {{max}} حرفًا'
                    }
                },
                medicalAnalysis: {
                    title: 'التحليل الطبي',
                    add: 'إضافة تحليل طبي',
                    name: 'الاسم',
                    description: 'الوصف',
                    result: 'النتيجة',
                    errors: {
                        nameRequired: 'اسم التحليل مطلوب',
                        nameMaxLength: 'اسم التحليل يجب ألا يتجاوز {{max}} حرفًا',
                        descriptionMaxLength: 'الوصف يجب ألا يتجاوز {{max}} حرفًا'
                    }
                },
                prescriptions: {
                    title: 'الوصفات الطبية',
                    tableTitle: 'جدول الوصفات الطبية',
                    tableSubtitle: 'عرض وإدارة جميع الوصفات الطبية للمرضى',
                    addPrescription: 'إضافة وصفة طبية',
                    addPrescriptionSubtitle: 'إنشاء وصفة طبية جديدة لمريض',
                    editPrescription: 'تعديل وصفة طبية',
                    deletePrescription: 'حذف وصفة طبية',
                    sendWhatsApp: 'إرسال عبر واتساب',
                    printPrescription: 'طباعة الوصفة الطبية',
                    prescriptionDetails: 'تفاصيل الوصفة الطبية',
                    dateIssued: 'تاريخ الإصدار',
                    diagnosis: 'التشخيص',
                    patientName: 'اسم المريض',
                    patientData: 'بيانات المريض',
                    noPrescriptionsFound: 'لم يتم العثور على وصفات طبية',
                    deleteConfirmation: 'هل أنت متأكد من رغبتك في حذف هذه الوصفة الطبية؟ لا يمكن التراجع عن هذا الإجراء.',
                    pdfOpenedForPrinting: 'تم فتح ملف PDF للوصفة الطبية للطباعة',
                    prescriptionSentViaWhatsApp: 'تم إرسال الوصفة الطبية عبر واتساب بنجاح',
                    selectPatient: 'اختر مريض',
                    currentPatient: 'المريض الحالي',
                    appointmentAndDiagnosis: 'الموعد والتشخيص',
                    medicalHistory: 'التاريخ الطبي',
                    drugsAndAnalyses: 'الأدوية والتحاليل',
                    availableDrugs: 'الأدوية المتاحة',
                    selectedDrugs: 'الأدوية المختارة',
                    availableAnalyses: 'التحاليل المتاحة',
                    selectedAnalyses: 'التحاليل المختارة',
                    addDrug: 'إضافة دواء جديد',
                    deleteDrug: 'حذف الدواء',
                    deleteItemConfirmation: 'هل أنت متأكد من رغبتك في حذف {{item}}؟ لا يمكن التراجع عن هذا الإجراء.',
                    examineWithAi: 'افحص باستخدام الذكاء الاصطناعي',
                    askAi: 'اسأل الذكاء الاصطناعي',
                    examinationWithAi: 'التشخيص باستخدام الذكاء الاصطناعي',
                    symptomsDescription: 'وصف أعراض المريض',
                    examine: 'افحص',
                    examinationTypes: {
                        firstTime: 'المرة الأولى',
                        followUp: 'متابعة'
                    },
                    examinationRecommendation: 'التوصية الطبية (باستخدام الذكاء الاصطناعي)',
                    defaultDoseAndUnit: '500 ملغ',
                    defaultFrequencyAndRoute: 'تناول 2 مرات يوميًا',
                    defaultInstructions: 'تناول مع الطعام',
                    searchDrugs: 'البحث عن أدوية',
                    noDrugsSelected: 'لم يتم اختيار أدوية',
                    dosageForm: 'شكل الجرعة',
                    doseAndUnit: 'الجرعة والوحدة',
                    frequencyAndRoute: 'التكرار والطريقة',
                    instructions: 'التعليمات',
                    drugs: 'الأدوية',
                    medicalAnalyses: 'التحاليل الطبية',
                    searchAnalyses: 'البحث عن تحاليل',
                    noAnalysesSelected: 'لم يتم اختيار تحاليل',
                    result: 'النتيجة',
                    addMedicalAnalysis: 'إضافة تحليل طبي',
                    deleteMedicalAnalysis: 'حذف تحليل طبي',
                    medicalHistoryForPatient: 'التاريخ الطبي للمريض',
                    noDiagnosisProvided: 'لم يتم تقديم تشخيص',
                    noMedicalHistoryAvailable: 'لا يوجد تاريخ طبي متاح',
                    loadingMedicalHistory: 'جارٍ تحميل التاريخ الطبي...',
                    errors: {
                        patientRequired: 'يجب اختيار موعد مريض',
                        symptomsRequired: 'يجب إدخال الأعراض',
                        diagnosisMaxLength: 'التشخيص يجب ألا يتجاوز {{max}} حرفًا',
                        promptRequired: 'المدخل مطلوب',
                        invalidDosageForm: 'شكل جرعة غير صالح',
                        pageNumberMin: 'رقم الصفحة يجب أن يكون أكبر من أو يساوي 1',
                        pageSizeMin: 'حجم الصفحة يجب أن يكون أكبر من أو يساوي 1',
                        deletePrescriptionError: 'حدث خطأ أثناء حذف الوصفة الطبية',
                        sendWhatsAppError: 'حدث خطأ أثناء إرسال الوصفة الطبية عبر واتساب',
                        retrievePdfError: 'فشل استرجاع ملف PDF للوصفة الطبية',
                        failedToGetResponse: 'فشل الحصول على الرد، حاول مرة أخرى',
                        fetchPdfError: 'حدث خطأ أثناء جلب ملف PDF للوصفة الطبية',
                        failedToAddDrug: 'فشل إضافة الدواء',
                        failedToAddDrugTryAgain: 'فشل إضافة الدواء. الرجاء المحاولة مرة أخرى.',
                        failedToAddMedicalAnalysis: 'فشل إضافة التحليل الطبي',
                        failedToAddMedicalAnalysisTryAgain: 'فشل إضافة التحليل الطبي. الرجاء المحاولة مرة أخرى.'
                    }
                },
                common: {
                    switchLanguage: 'تغيير اللغة',
                    switchToRTL: 'التحويل إلى اليمين إلى اليسار',
                    switchToLTR: 'التحويل إلى اليسار إلى اليمين',
                    lightMode: 'الوضع الفاتح',
                    darkMode: 'الوضع الداكن',
                    actions: 'الإجراءات',
                    processing: 'جارٍ المعالجة...',
                    ok: 'موافق',
                    save: 'حفظ',
                    complete: 'إكمال',
                    cancel: 'إلغاء',
                    loading: 'جارٍ التحميل...',
                    loadingData: 'جارٍ تحميل البيانات...',
                    notAvailable: 'غير متاح',
                    add: 'إضافة',
                    edit: 'تعديل',
                    delete: 'حذف',
                    close: 'إغلاق',
                    submit: 'تأكيد',
                    filterPanel: 'شريط البحث',
                    applyFilters: 'تطبيق الفلاتر',
                    clearFilters: 'مسح الفلاتر',
                    clear: 'مسح',
                    search: 'بحث',
                    active: 'نشط',
                    inactive: 'غير نشط',
                    all: 'الكل',
                    yes: 'نعم',
                    no: 'لا',
                    notFound: {
                        icon: 'أيقونة 404',
                        title: 'الصفحة غير موجودة',
                        description: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها. حاول العودة إلى الصفحة الرئيسية.'
                    },
                    unauthorized: {
                        icon: 'أيقونة القفل',
                        title: 'الوصول غير المصرح به',
                        description: 'ليس لديك إذن للوصول إلى هذه الصفحة. الرجاء التواصل مع المدير إذا كنت تعتقد أن هذا خطأ.'
                    },
                    backToHome: 'العودة إلى الرئيسية',
                    firstName: 'الاسم الأول',
                    lastName: 'اسم العائلة',
                    email: 'البريد الإلكتروني',
                    phoneNumber: 'رقم الهاتف',
                    password: 'كلمة المرور',
                    role: 'الوظيفة',
                    roles: 'الوظائف',
                    isActive: 'نشط',
                    submitting: 'جاري المعالجة...',
                    pagination: {
                        rowsPerPage: 'عدد الصفوف لكل صفحة',
                        displayedRows: 'الصفوف {{from}}-{{to}} من {{count}}'
                    },
                    errors: {
                        required: 'هذا الحقل مطلوب'
                    }
                }
            }
        }
    },
    lng: LanguageCode.EN,
    fallbackLng: LanguageCode.EN,
    interpolation: { escapeValue: false }
});

export default i18n;